import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BrandMark from '../common/BrandMark';
import { useRegisterAction } from '../../hooks/useRegisterAction';
import styles from './LandingFooter.module.css';

const SOCIALS = [
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/repbuddy.io/',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" />
        <circle cx="17.4" cy="6.6" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61590863405600',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M13.5 21v-8h2.6l.4-3h-3V8.1c0-.86.28-1.45 1.53-1.45H16.6V4.1C16.3 4.06 15.4 4 14.4 4c-2.1 0-3.6 1.28-3.6 3.64V10H8.2v3h2.6v8h2.7z" />
      </svg>
    ),
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@repbuddy.io',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.1 4c.32 1.9 1.62 3.36 3.5 3.62v2.45c-1.28 0-2.48-.4-3.5-1.08v5.4a5.13 5.13 0 1 1-5.13-5.13c.3 0 .6.03.88.08v2.52a2.68 2.68 0 1 0 1.87 2.55V4h2.38z" />
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/repbuddy',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M6.94 8.6V19H4V8.6h2.94zM5.47 4a1.72 1.72 0 1 0 0 3.44 1.72 1.72 0 0 0 0-3.44zM20 19h-2.94v-5.16c0-1.3-.47-2.18-1.63-2.18-.89 0-1.42.6-1.65 1.18-.09.2-.11.5-.11.79V19H10.7s.04-9.02 0-10.4h2.94v1.47c.39-.6 1.09-1.46 2.66-1.46 1.94 0 3.4 1.27 3.4 4z" />
      </svg>
    ),
  },
  {
    name: 'X',
    href: 'https://x.com/RepBuddyio',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.5 3h3l-6.55 7.48L21.7 21h-5.9l-4.2-5.5L6.6 21H3.6l7-8L3.3 3h6l3.8 5.02zm-1.05 16.2h1.66L7.62 4.7H5.85z" />
      </svg>
    ),
  },
];

// `registerPath` sets where the register CTAs go. Defaults to the public /register flow;
// the private founding page passes "/founding" to keep visitors in that flow.
// `showCta` toggles the repeat trial-offer CTA above the footer - the founding page hides
// it so its footer sits directly below the page's own content.
export default function LandingFooter({ registerPath = '/register', showCta = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const register = useRegisterAction(registerPath);
  const year = new Date().getFullYear();
  // Official logo at /logo.png; fall back to the lettermark if it's missing.
  const [logoOk, setLogoOk] = useState(true);

  // Section links live on the home page - from another route, go home then scroll.
  function goToSection(id) {
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
  }

  return (
    <>
      {/* Repeat trial offer - hidden on the founding page (showCta=false) */}
      {showCta && (
      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <BrandMark size={56} className={styles.ctaMark} />
          <h2 className={styles.ctaHeading}>Stop choosing between speed and quality.</h2>
          <p className={styles.ctaSub}>
            {register.isAuthed
              ? 'You’re all set. Jump back into RepBuddy anytime.'
              : 'Sign up free and start finding verified leads today.'}
          </p>
          <div className={styles.ctaButtons}>
            <button className={styles.ctaButton} onClick={register.go}>
              {register.label}
            </button>
          </div>
          <p className={styles.ctaTrial}>7 days · 30 verified leads · no credit card</p>
        </div>
      </section>
      )}

      {/* Site footer */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.brandCol}>
            <button className={styles.logo} onClick={() => navigate('/')} aria-label="RepBuddy home">
              {logoOk ? (
                <img
                  className={styles.logoImg}
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="RepBuddy"
                  onError={() => setLogoOk(false)}
                />
              ) : (
                <span className={styles.logoMark}>R</span>
              )}
              <span className={styles.logoName}>RepBuddy</span>
            </button>
            <p className={styles.brandLine}>Human-quality prospecting at AI speed.</p>

            <div className={styles.socials}>
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  className={styles.social}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  title={s.name}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          <div className={styles.linkCol}>
            <span className={styles.colTitle}>Product</span>
            <button className={styles.footLink} onClick={() => navigate('/product')}>Workflows</button>
            <button className={styles.footLink} onClick={() => navigate('/guide')}>Help Center</button>
            <button className={styles.footLink} onClick={() => goToSection('how-it-works')}>How it works</button>
            <button className={styles.footLink} onClick={() => navigate('/pricing')}>Pricing</button>
            <button className={styles.footLink} onClick={() => navigate('/compare')}>Compare</button>
          </div>

          <div className={styles.linkCol}>
            <span className={styles.colTitle}>Company</span>
            <button className={styles.footLink} onClick={() => navigate('/about')}>About us</button>
            <button className={styles.footLink} onClick={() => navigate('/faq')}>FAQ</button>
            {register.isAuthed ? (
              <button className={styles.footLink} onClick={register.go}>Go to app</button>
            ) : (
              <>
                <button className={styles.footLink} onClick={() => navigate('/login')}>Log in</button>
                <button className={styles.footLink} onClick={register.go}>Sign up free</button>
              </>
            )}
          </div>

          <div className={styles.linkCol}>
            <span className={styles.colTitle}>Contact</span>
            <button className={styles.footLink} onClick={() => navigate('/contact')}>Contact us</button>
            <button className={styles.footLink} onClick={() => navigate('/feedback')}>Share feedback</button>
            <a className={styles.footLink} href="mailto:support@repbuddy.io">support@repbuddy.io</a>
            <span className={styles.footMuted}>We reply within one business day.</span>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <span>© {year} RepBuddy. All rights reserved.</span>
          <nav className={styles.legalLinks} aria-label="Legal">
            <button className={styles.legalLink} onClick={() => navigate('/privacy')}>Privacy policy</button>
            <button className={styles.legalLink} onClick={() => navigate('/terms')}>Terms &amp; conditions</button>
            <button className={styles.legalLink} onClick={() => navigate('/cookies')}>Cookie policy</button>
            <button className={styles.legalLink} onClick={() => navigate('/request-removal')}>Do not sell or share my info</button>
          </nav>
        </div>
      </footer>
    </>
  );
}
